// src/stores/person.ts
import { defineStore } from 'pinia'
import { ref, readonly } from 'vue'
import { getPersonService } from '@/services/serviceFactory'
import { PersonSearch } from '@/services/person/PersonSearch'
import { PersonService } from '@/services/person/PersonService'
import { useSession } from '@/stores/authStore'
import { type Person } from '@/types/Person.ts'

export const usePersonStoreV2 = defineStore('person.v2', () => {
  // State
  const loading = ref(false)
  const cache = ref({} as Record<string, Person>)

  // Service instance - we'll get it lazily
  let _service: PersonService | null = null

  async function getService(): Promise<PersonService> {
    if (!_service) {
      _service = await getPersonService()
    }
    return _service
  }

  // Getters
  const getSearch = async () => {
    // Get session from authStore
    const session = useSession()

    // Wait for session to be ready
    let attempts = 0
    while (!session.tenantId && attempts < 50) {
      // Wait up to 5 seconds
      await new Promise((resolve) => setTimeout(resolve, 100))
      attempts++
    }

    if (!session.tenantId) {
      throw new Error('Session not ready: tenantId is required')
    }

    const service = await getService()
    return new PersonSearch(session, service['firestore'])
  }

  // Actions
  async function get(id: string): Promise<Person | null> {
    // Check cache first
    if (cache.value[id]) return cache.value[id]

    // Fetch from Firestore using service
    const service = await getService()
    const person = await service.get(id)
    if (person) cache.value[id] = person
    return person
  }

  async function create(data: Partial<Person>): Promise<string> {
    loading.value = true
    try {
      const service = await getService()
      return await service.create(data)
    } finally {
      loading.value = false
    }
  }

  async function update(id: string, data: Partial<Person>): Promise<void> {
    loading.value = true
    try {
      const service = await getService()
      await service.update(id, data)
      // Clear cache for this item
      delete cache.value[id]
    } finally {
      loading.value = false
    }
  }

  async function remove(id: string): Promise<void> {
    loading.value = true
    try {
      const service = await getService()
      await service.delete(id)
      // Clear cache for this item
      delete cache.value[id]
    } finally {
      loading.value = false
    }
  }

  function clearCache() {
    cache.value = {}
  }

  return {
    // State
    loading: readonly(loading),
    cache: readonly(cache),

    // Getters
    search: getSearch,

    // Actions
    get,
    create,
    update,
    remove,
    clearCache,
  }
})

// Export the old name for backward compatibility
export const usePersonStore = usePersonStoreV2
